import { Component, ElementRef, ViewChild, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NurSignal } from '../../models/wallpaper.model';
import { AppStateService } from '../../services/app-state.service';
import { WallpaperService } from '../../services/wallpaper.service';

/* ─── Types ─── */
interface ActiveFilters {
  room: string | null;
  style: string | null;
  mood: string | null;
  color: string | null;
}

interface NurGuidance {
  id: string;
  text: string;
  followUp?: string;
  chips?: { id: string; label: string }[];
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  resultIds?: string[];
}

type BubbleState = 'idle' | 'nudge';

/* ─── Chip data ─── */
const ROOM_CHIPS = [
  { id: 'Living Room', label: 'Living Room' },
  { id: 'Bedroom', label: 'Bedroom' },
  { id: 'Kids Room', label: 'Kids Room' },
  { id: 'Kitchen', label: 'Kitchen' },
  { id: 'Dining Room', label: 'Dining' },
  { id: 'Office', label: 'Office' },
];

const STYLE_CHIPS = [
  { id: 'Florals', label: 'Floral' },
  { id: 'Modern', label: 'Geometric' },
  { id: 'Classic & Vintage', label: 'Damask' },
  { id: 'Art', label: 'Art' },
  { id: 'Nature', label: 'Nature' },
  { id: 'Scenic', label: 'Scenic' },
  { id: 'Minimal', label: 'Minimal' },
  { id: 'Abstract', label: 'Abstract' },
];

const MOOD_CHIPS = [
  { id: 'calm', label: 'Calm' },
  { id: 'luxury', label: 'Luxury' },
  { id: 'statement', label: 'Bold' },
  { id: 'cozy', label: 'Cozy' },
  { id: 'playful', label: 'Playful' },
  { id: 'elegant', label: 'Elegant' },
];

const COLOR_CHIPS = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'neutral', label: 'Neutral' },
  { id: 'warm', label: 'Warm' },
  { id: 'cool', label: 'Cool' },
  { id: 'bold', label: 'Colorful' },
];

@Component({
  selector: 'app-nur',
  templateUrl: './nur.component.html',
  styleUrls: ['./nur.component.css']
})
export class NurComponent implements OnInit, OnDestroy {

  @ViewChild('scrollRef') scrollRef!: ElementRef;
  @ViewChild('wallFileInput') wallFileInput!: ElementRef;
  @ViewChild('refFileInput') refFileInput!: ElementRef;

  /* ── Panel state ── */
  isOpen = false;
  panelState: 'normal' | 'expanded' = 'normal';

  /* ── Filters ── */
  filters: ActiveFilters = { room: null, style: null, mood: null, color: null };

  /* ── Results (real API data) ── */
  results: any[] = [];
  wallpaperCache = new Map<string, any>();

  /* ── Chat ── */
  chatHistory: ChatMessage[] = [];
  isAiLoading = false;
  guidance: NurGuidance | null = null;

  /* ── Input ── */
  inputValue = '';

  /* ── Phase ── */
  phase: 'pick-room' | 'browsing' = 'pick-room';

  /* ── Wall size & images ── */
  wallSize: string | null = null;
  searchKeywords: string | null = null;
  referenceImage: string | null = null;
  wallImage: string | null = null;
  previewWallpaper: any = null;

  /* ── Upload state ── */
  isAnalyzing = false;
  analysisResult: string | null = null;
  activeUploadType: 'wall' | 'reference' | null = null;

  /* ── Bubble state ── */
  bubbleState: BubbleState = 'idle';
  nudgeMessage = '';

  /* ── Similar tracking ── */
  similarProductId: string | null = null;

  /* ── Filter panel ── */
  showFilterPanel = false;

  /* ── Master data ── */
  spaces: any[] = [];
  styles: any[] = [];
  colors: any[] = [];
  themes: any[] = [];

  /* ── Loading ── */
  isLoading = false;

  /* ── Chip arrays (exposed to template) ── */
  readonly roomChips = ROOM_CHIPS;
  readonly styleChips = STYLE_CHIPS;
  readonly moodChips = MOOD_CHIPS;
  readonly colorChips = COLOR_CHIPS;

  /* ── Internal ── */
  private hasGreeted = false;
  private filterChangeCount = 0;
  private nudgeTimer: any;
  private subs = new Subscription();

  constructor(
    private router: Router,
    public state: AppStateService,
    private wallpaperService: WallpaperService,
    private cdRef: ChangeDetectorRef
  ) {}

  /* ═══════════════════════════════════════
     LIFECYCLE
     ═══════════════════════════════════════ */

  ngOnInit() {
    this.subs.add(
      this.state.isNurOpen$.subscribe(open => {
        this.isOpen = open;
        if (open) {
          this.panelState = 'normal';
          this.onPanelOpen();
        }
      })
    );

    this.subs.add(
      this.state.nurSignal$.subscribe(signal => {
        if (signal) this.handleSignal(signal);
      })
    );

    this.loadMasters();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    clearTimeout(this.nudgeTimer);
  }

  /* ═══════════════════════════════════════
     MASTERS
     ═══════════════════════════════════════ */

  loadMasters() {
    this.wallpaperService.getMasters().subscribe({
      next: (res: any) => {
        // API returns different shapes — try all known paths
        const masters = res?.masters || res?.data || res;
        this.spaces = masters?.spaces?.data || masters?.space || res?.space || [];
        this.styles = masters?.category?.data || masters?.category || res?.category || [];
        this.colors = masters?.colors?.data || masters?.color || res?.color || [];
        this.themes = masters?.themes?.data || masters?.['wallpaper-theme'] || res?.['wallpaper-theme'] || [];

        console.log('[NUR] Masters loaded — spaces:', this.spaces.length, 'styles:', this.styles.length);
        if (this.spaces.length > 0) console.log('[NUR] Sample space:', this.spaces[0]);
        if (this.styles.length > 0) console.log('[NUR] Sample style:', this.styles[0]);
      },
      error: (err) => {
        console.error('[NUR] Masters load failed', err);
      }
    });
  }

  /* ═══════════════════════════════════════
     PANEL OPEN / CLOSE
     ═══════════════════════════════════════ */

  onPanelOpen() {
    if (this.hasGreeted || this.chatHistory.length > 0) return;
    this.hasGreeted = true;

    let greeting: string;
    const route = this.router.url;

    if (route.startsWith('/pdp') || route.startsWith('/wallpaper/')) {
      greeting = "Tell me about your space \u2014 I'll find options that complement this style.";
    } else if (route.startsWith('/listing')) {
      greeting = "Hi! I see you're browsing. What are you looking for in a wallpaper?";
    } else {
      greeting = "Hi! How can I help you find the right wallpaper for your space?";
    }

    this.chatHistory.push({ id: this.uid(), role: 'assistant', content: greeting });
    this.guidance = {
      id: this.uid(),
      text: '',
      followUp: 'Which space are you decorating?',
      chips: ROOM_CHIPS.map(c => ({ id: c.id, label: c.label })),
    };
    this.scrollToBottom();
  }

  openPanel() {
    this.dismissNudge();
    if (!this.isOpen) {
      this.state.toggleNur();
    }
  }

  close() {
    this.state.closeNur();
  }

  toggleSize() {
    this.panelState = this.panelState === 'expanded' ? 'normal' : 'expanded';
  }

  /* ═══════════════════════════════════════
     BUBBLE / NUDGE
     ═══════════════════════════════════════ */

  showNudge(message: string) {
    clearTimeout(this.nudgeTimer);
    this.bubbleState = 'nudge';
    this.nudgeMessage = message;
    this.nudgeTimer = setTimeout(() => {
      this.bubbleState = 'idle';
      this.nudgeMessage = '';
      this.cdRef.detectChanges();
    }, 6000);
    this.cdRef.detectChanges();
  }

  dismissNudge() {
    clearTimeout(this.nudgeTimer);
    this.bubbleState = 'idle';
    this.nudgeMessage = '';
  }

  /* ═══════════════════════════════════════
     SIGNAL HANDLER
     ═══════════════════════════════════════ */

  handleSignal(signal: NurSignal) {
    switch (signal.type) {

      case 'listing-initial-browse':
        if (!this.isOpen) this.showNudge('Looking for something specific? I can help narrow by room, style, or mood.');
        break;

      case 'plp-scroll-nudge':
        if (!this.isOpen) this.showNudge('Need help narrowing this down?');
        break;

      case 'plp-deep-scroll':
        if (!this.isOpen) this.showNudge('Too many options? I can help narrow down by room, style, or wall size.');
        break;

      case 'plp-behavior-pattern':
        if (signal.data?.patternStyle) {
          const styleLabel = STYLE_CHIPS.find(s => s.id === signal.data!.patternStyle)?.label || signal.data.patternStyle;
          this.filters = { ...this.filters, style: signal.data.patternStyle };
          this.phase = 'browsing';
          this.state.updateContext({ selected_category: signal.data.patternStyle });
          this.loadWallpapers(`Looks like you prefer ${styleLabel?.toLowerCase()} \u2014 here are your updated results.`);
          if (!this.isOpen) this.showNudge(`Looks like you prefer ${styleLabel?.toLowerCase()} \u2014 I\u2019ve updated your results!`);
        }
        break;

      case 'pdp-dwell':
        if (!this.isOpen) { this.openPanel(); this.hasGreeted = true; }
        setTimeout(() => {
          const msg = 'You seem interested in this one \u2014 is this for a large wall?';
          if (this.phase === 'pick-room') this.phase = 'browsing';
          this.chatHistory.push({ id: this.uid(), role: 'assistant', content: msg,
            resultIds: this.results.map(w => String(w.id)),
          });
          this.guidance = { id: this.uid(), text: '', chips: [
            { id: '10x12', label: 'Yes, large wall' },
            { id: '8x10', label: 'Medium wall' },
            { id: '6x8', label: 'Small / accent wall' },
          ]};
          this.scrollToBottom();
        }, 300);
        break;

      case 'product-click':
        if (this.isOpen && signal.data?.wallpaperId) {
          const wp = this.wallpaperCache.get(signal.data.wallpaperId);
          const comment = wp
            ? `Nice choice \u2014 ${wp.name} works beautifully as a feature wall.`
            : 'Nice choice \u2014 this works well in most spaces.';
          this.chatHistory.push({
            id: this.uid(), role: 'assistant', content: comment,
            resultIds: this.results.map(w => String(w.id)),
          });
          this.guidance = { id: this.uid(), text: '', chips: [
            { id: 'similar', label: 'Show similar' },
            { id: 'continue', label: 'Keep browsing' },
          ]};
          this.scrollToBottom();
        }
        break;

      case 'rapid-product-switching':
        if (!this.isOpen) { this.openPanel(); this.hasGreeted = true; }
        setTimeout(() => {
          this.chatHistory.push({
            id: this.uid(), role: 'assistant',
            content: 'Are you exploring options for a feature wall? I can help narrow it down \u2014 just tell me the room and your wall size.',
            resultIds: this.results.map(w => String(w.id)),
          });
          this.guidance = { id: this.uid(), text: '', chips: [
            { id: '10x12', label: 'Large feature wall' },
            { id: '8x10', label: 'Medium wall' },
            { id: 'refine', label: 'Refine style' },
          ]};
          this.scrollToBottom();
        }, 300);
        break;

      case 'space-selected':
        if (signal.data?.space) {
          if (!this.isOpen) this.state.toggleNur();
          this.hasGreeted = true;
          const room = signal.data.space;
          this.filters = { ...this.filters, room };
          this.phase = 'browsing';
          this.state.updateContext({ selected_space: room });
          this.loadWallpapers(
            `I see you\u2019re exploring ${room.toLowerCase()} wallpapers! What style appeals to you?`,
            STYLE_CHIPS.map(c => ({ id: c.id, label: c.label }))
          );
        }
        break;

      case 'category-selected': {
        if (!this.isOpen) this.state.toggleNur();
        this.hasGreeted = true;
        const cat = signal.data?.category || 'wallpapers';
        this.filters = { ...this.filters, style: cat };
        this.phase = 'browsing';
        this.state.updateContext({ selected_category: cat });
        this.loadWallpapers(
          `Great pick! Here are ${cat.toLowerCase()} wallpapers to explore. Which space is this for?`,
          ROOM_CHIPS.map(c => ({ id: c.id, label: c.label }))
        );
        break;
      }

      case 'pdp-back-to-listing':
        this.chatHistory.push({
          id: this.uid(), role: 'assistant',
          content: 'Welcome back. Want me to refine the results based on wall size or style?',
        });
        this.guidance = { id: this.uid(), text: '', chips: [
          { id: '6x8', label: '6 \u00D7 8 ft' },
          { id: '8x10', label: '8 \u00D7 10 ft' },
          { id: '10x12', label: '10 \u00D7 12 ft' },
          { id: 'refine', label: 'Refine style' },
        ]};
        this.scrollToBottom();
        break;

      case 'wishlist-add':
        if (!this.isOpen) this.showNudge('Love that! I can find more wallpapers like this for you.');
        break;

      case 'pdp-add-to-cart':
        if (!this.isOpen) this.showNudge('Great choice! Want help styling the rest of your space?');
        break;

      case 'home-idle':
        if (!this.isOpen) this.showNudge('Not sure where to start? Tell me your room and I\'ll guide you.');
        break;

      case 'listing-filter-change':
        if (!this.isOpen) this.showNudge('Exploring lots of options? I can narrow this down with a few quick questions.');
        break;
    }
  }

  /* ═══════════════════════════════════════
     BUILD GUIDANCE (next question)
     ═══════════════════════════════════════ */

  buildGuidance(ack: string, f: ActiveFilters): NurGuidance {
    if (!f.room) {
      return {
        id: this.uid(), text: ack,
        followUp: 'Which space are you decorating?',
        chips: ROOM_CHIPS.map(c => ({ id: c.id, label: c.label })),
      };
    }
    if (!f.style) {
      const hints: Record<string, string> = {
        'Bedroom': 'Bedrooms suit calm, elegant styles.',
        'Living Room': 'Living rooms can handle bold or minimal \u2014 your call.',
        'Kids Room': 'Fun, playful patterns work beautifully here.',
        'Kitchen': 'Fresh, open patterns suit kitchens well.',
        'Dining Room': 'Dining rooms love statement designs.',
        'Office': 'Clean, focused designs work best here.',
      };
      const hint = f.room ? (hints[f.room] || '') : '';
      return {
        id: this.uid(), text: ack + (hint ? ` ${hint}` : ''),
        followUp: 'What style appeals to you?',
        chips: STYLE_CHIPS.map(c => ({ id: c.id, label: c.label })),
      };
    }
    if (!f.mood) {
      return {
        id: this.uid(), text: ack,
        followUp: 'What mood are you going for?',
        chips: MOOD_CHIPS.map(c => ({ id: c.id, label: c.label })),
      };
    }
    if (!f.color) {
      return {
        id: this.uid(), text: ack,
        followUp: 'Any colour preference?',
        chips: COLOR_CHIPS.map(c => ({ id: c.id, label: c.label })),
      };
    }
    return {
      id: this.uid(), text: ack,
      followUp: 'Want to refine further or see something different?',
      chips: [
        { id: 'refine', label: 'Refine more' },
        { id: 'upload-wall', label: 'Upload wall photo' },
        { id: 'different', label: 'Show something different' },
      ],
    };
  }

  /* ═══════════════════════════════════════
     APPLY FILTERS → calls loadWallpapers
     ═══════════════════════════════════════ */

  applyFilters(newFilters: ActiveFilters, ack: string, newWallSize?: string | null) {
    this.filters = newFilters;
    if (newWallSize !== undefined) this.wallSize = newWallSize;

    if (newFilters.room) this.state.updateContext({ selected_space: newFilters.room });
    if (newFilters.style) this.state.updateContext({ selected_category: newFilters.style });
    if (newFilters.mood) this.state.updateContext({ selected_mood: newFilters.mood });
    if (this.phase === 'pick-room') this.phase = 'browsing';

    // Repeated filter changes nudge
    this.filterChangeCount++;
    if (this.filterChangeCount === 4) {
      this.filterChangeCount = 0;
      this.loadWallpapers(
        "You've been exploring quite a few options \u2014 would you like me to suggest wallpapers based on your room style instead?",
        ROOM_CHIPS.map(c => ({ id: c.id, label: c.label }))
      );
      return;
    }

    this.loadWallpapers(ack);
  }

  /* ═══════════════════════════════════════
     REMOVE FILTER
     ═══════════════════════════════════════ */

  removeFilter(type: 'room' | 'style' | 'mood' | 'color' | 'wallSize' | 'keywords') {
    if (type === 'wallSize') {
      this.wallSize = null;
    } else if (type === 'keywords') {
      this.searchKeywords = null;
    } else {
      this.filters = { ...this.filters, [type]: null };
    }
    const label = type === 'wallSize' ? 'wall size' : type === 'keywords' ? 'search keywords' : type;
    this.chatHistory.push({ id: this.uid(), role: 'user', content: `Removed ${label} filter` });
    this.loadWallpapers(`Removed ${label} filter. Here are updated results.`);
  }

  clearAllFilters() {
    this.filters = { room: null, style: null, mood: null, color: null };
    this.wallSize = null;
    this.searchKeywords = null;
    this.chatHistory.push({ id: this.uid(), role: 'user', content: 'Cleared all filters' });
    this.loadWallpapers('All filters cleared. Showing fresh results.', ROOM_CHIPS.map(c => ({ id: c.id, label: c.label })));
  }

  /* ═══════════════════════════════════════
     LOAD WALLPAPERS (real Solr API)
     ═══════════════════════════════════════ */

  /**
   * Calls searchWallpapers API, caches results, and adds a chat message with inline thumbnails.
   * @param ack  Acknowledgment text for NUR response. If null, results load silently.
   * @param overrideChips  Override the default guidance chips (used by signal handlers).
   */
  loadWallpapers(ack?: string | null, overrideChips?: { id: string; label: string }[]) {
    this.isLoading = true;

    // Map filter display names → master IDs for API
    const spaceId = this.filters.room
      ? this.findMasterId(this.spaces, this.filters.room)
      : null;
    const catId = this.filters.style
      ? this.findMasterId(this.styles, this.filters.style)
      : null;

    // Build searchtext from keywords, mood, color — also include room/style name if ID wasn't found
    const searchParts: string[] = [];
    if (this.filters.room && !spaceId) searchParts.push(this.filters.room);
    if (this.filters.style && !catId) searchParts.push(this.filters.style);
    if (this.searchKeywords) searchParts.push(this.searchKeywords);
    if (this.filters.mood) searchParts.push(this.filters.mood);
    if (this.filters.color) searchParts.push(this.filters.color);
    const searchtext = searchParts.length > 0 ? searchParts.join(' ') : undefined;

    this.wallpaperService.searchWallpapers({
      space: spaceId ? String(spaceId) : undefined,
      category: catId ? String(catId) : undefined,
      searchtext,
      page: 1,
      images: 999,
    }).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.results = res.results || [];

        // Cache every result for fast lookup
        this.results.forEach(wp => this.wallpaperCache.set(String(wp.id), wp));

        const resultIds = this.results.map(wp => String(wp.id));

        if (ack) {
          // Build guidance for next conversational step
          const g = overrideChips
            ? { text: ack, followUp: undefined, chips: overrideChips, id: this.uid() }
            : this.buildGuidance(ack, this.filters);

          const chatText = [g.text, g.followUp].filter(Boolean).join(' ');
          if (chatText) {
            this.chatHistory.push({
              id: this.uid(), role: 'assistant', content: chatText,
              resultIds: resultIds.length > 0 ? resultIds : undefined,
            });
          }
          this.guidance = (g.chips && g.chips.length)
            ? { id: this.uid(), text: '', chips: g.chips, followUp: g.followUp }
            : null;
        }

        this.scrollToBottom();
        this.cdRef.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        if (ack) {
          this.chatHistory.push({
            id: this.uid(), role: 'assistant',
            content: ack + ' I had trouble loading results — try refining your search.',
          });
          const g = this.buildGuidance(ack, this.filters);
          this.guidance = g.chips ? { id: this.uid(), text: '', chips: g.chips } : null;
        }
        this.scrollToBottom();
        this.cdRef.detectChanges();
      }
    });
  }

  /** Find master item ID by matching display_name, name, or slug (case-insensitive, with partial match fallback). */
  private findMasterId(list: any[], name: string): number | null {
    if (!list || list.length === 0 || !name) return null;
    const lower = name.toLowerCase().trim();

    // 1. Exact match on display_name or name
    let found = list.find((item: any) =>
      (item.display_name || '').toLowerCase() === lower ||
      (item.name || '').toLowerCase() === lower
    );
    if (found) return found.id;

    // 2. Slug match (e.g. "living-room" matches "Living Room")
    const slug = lower.replace(/\s+/g, '-');
    found = list.find((item: any) =>
      (item.slug || '').toLowerCase() === slug ||
      (item.name || '').toLowerCase().replace(/\s+/g, '-') === slug
    );
    if (found) return found.id;

    // 3. Partial / includes match
    found = list.find((item: any) =>
      (item.display_name || '').toLowerCase().includes(lower) ||
      (item.name || '').toLowerCase().includes(lower) ||
      lower.includes((item.display_name || '').toLowerCase()) ||
      lower.includes((item.name || '').toLowerCase())
    );
    if (found) return found.id;

    console.warn(`[NUR] Could not find master ID for "${name}" in list of ${list.length} items`);
    return null;
  }

  /* ═══════════════════════════════════════
     CHIP HANDLERS
     ═══════════════════════════════════════ */

  handleFilterToggle(type: keyof ActiveFilters, value: string) {
    const isRemove = this.filters[type] === value;
    const newFilters = { ...this.filters, [type]: isRemove ? null : value };
    const label = value.charAt(0).toUpperCase() + value.slice(1);
    if (!isRemove) {
      this.chatHistory.push({ id: this.uid(), role: 'user', content: label });
    }
    this.applyFilters(
      newFilters,
      isRemove
        ? `Removed ${label.toLowerCase()} filter. Want to try a different option?`
        : `Showing ${label.toLowerCase()} wallpapers.`
    );
  }

  nurSay(text: string, chips?: { id: string; label: string }[], resultIds?: string[]) {
    const ids = resultIds !== undefined
      ? (resultIds.length ? resultIds : undefined)
      : (this.results.length > 0 ? this.results.map(w => String(w.id)) : undefined);
    this.chatHistory.push({
      id: this.uid(), role: 'assistant', content: text, resultIds: ids,
    });
    this.guidance = chips?.length ? { id: this.uid(), text: '', chips } : null;
    this.scrollToBottom();
  }

  handleGuidanceChip(chipId: string) {
    if (ROOM_CHIPS.some(c => c.id === chipId)) { this.handleFilterToggle('room', chipId); return; }
    if (STYLE_CHIPS.some(c => c.id === chipId)) { this.handleFilterToggle('style', chipId); return; }
    if (MOOD_CHIPS.some(c => c.id === chipId)) { this.handleFilterToggle('mood', chipId); return; }
    if (COLOR_CHIPS.some(c => c.id === chipId)) { this.handleFilterToggle('color', chipId); return; }

    if (['6x8', '8x10', '10x12', 'custom'].includes(chipId)) {
      const sizeLabel = chipId === '10x12' ? '10 \u00D7 12 ft' : chipId === '8x10' ? '8 \u00D7 10 ft' : chipId === '6x8' ? '6 \u00D7 8 ft' : 'Custom size';
      const sizeAck = chipId === '10x12' ? 'Large wall \u2014 statement designs work beautifully here.'
        : chipId === '8x10' ? 'Good size \u2014 most patterns work well here.'
        : chipId === '6x8' ? 'Smaller wall \u2014 lighter patterns will open up the space.'
        : 'Custom size \u2014 just let me know the dimensions later.';
      this.chatHistory.push({ id: this.uid(), role: 'user', content: sizeLabel });
      this.applyFilters(this.filters, sizeAck, chipId);
      return;
    }

    if (chipId === 'upload-ref') { this.refFileInput?.nativeElement?.click(); return; }
    if (chipId === 'upload-wall') { this.wallFileInput?.nativeElement?.click(); return; }

    if (chipId === 'skip') {
      this.chatHistory.push({ id: this.uid(), role: 'user', content: 'No reference image, just browse' });
      this.referenceImage = 'skipped';
      const g = this.buildGuidance('No problem \u2014 let\u2019s keep refining.', this.filters);
      this.nurSay([g.text, g.followUp].filter(Boolean).join(' '), g.chips);
      return;
    }

    if (chipId === 'similar') {
      this.chatHistory.push({ id: this.uid(), role: 'user', content: 'Show similar' });
      // Re-search with current filters — API returns similar results
      this.loadWallpapers(
        'Here are similar wallpapers based on your current style.',
        [{ id: 'refine', label: 'Refine more' }, { id: 'different', label: 'Show something different' }]
      );
      return;
    }

    if (chipId === 'continue') {
      this.chatHistory.push({ id: this.uid(), role: 'user', content: 'Keep browsing' });
      this.nurSay('Take your time \u2014 I\u2019m here whenever you need me.');
      return;
    }

    if (chipId === 'refine') {
      this.chatHistory.push({ id: this.uid(), role: 'user', content: 'Refine results' });
      this.showFilterPanel = true;
      this.nurSay('Use the filters above to narrow down, or just tell me what to change.');
      return;
    }

    if (chipId === 'different') {
      this.chatHistory.push({ id: this.uid(), role: 'user', content: 'Show something different' });
      this.applyFilters({ room: this.filters.room, style: null, mood: null, color: null }, 'Here\u2019s a fresh set.');
      return;
    }

    if (chipId === 'done') {
      this.chatHistory.push({ id: this.uid(), role: 'user', content: 'These look good!' });
      this.nurSay('Great picks! Tap any wallpaper to see full details, or keep browsing.');
      return;
    }
  }

  /* ═══════════════════════════════════════
     TEXT INPUT → BACKEND AI
     ═══════════════════════════════════════ */

  send() {
    const text = this.inputValue.trim();
    if (!text || this.isAiLoading) return;
    this.inputValue = '';

    this.chatHistory.push({ id: this.uid(), role: 'user', content: text });
    this.isAiLoading = true;
    this.scrollToBottom();

    this.wallpaperService.detectFiltersAI(text).subscribe({
      next: (detected: any) => {
        this.isAiLoading = false;
        this.applyDetectedFilters(detected, text);
      },
      error: () => {
        this.isAiLoading = false;
        // Fallback: local text detection
        const detected = this.detectFiltersFromText(text);
        this.applyDetectedFilters(detected, text);
      }
    });
  }

  private applyDetectedFilters(detected: any, originalText: string) {
    if (detected.space) {
      this.filters.room = detected.space;
      this.state.updateContext({ selected_space: detected.space });
    }
    if (detected.style) {
      const style = this.styles.find((s: any) => s.name?.toLowerCase() === detected.style?.toLowerCase());
      if (style) this.filters.style = detected.style;
      else this.filters.style = detected.style; // use as-is even if not in masters
    }
    if (detected.mood) {
      this.filters.mood = detected.mood;
    }

    const wallSize = this.detectWallSize(originalText);
    if (wallSize) this.wallSize = wallSize;

    if (this.phase === 'pick-room') this.phase = 'browsing';

    // Build ack from detected
    let ack = '';
    if (detected.space) ack += `Looking at ${detected.space} wallpapers. `;
    if (detected.style) ack += `${detected.style} style. `;
    if (detected.mood) ack += `${detected.mood} mood. `;
    if (detected.keywords) ack += `Searching for "${detected.keywords}". `;
    if (!ack) ack = 'Let me find the best options for you. ';

    // Store keywords for API searchtext
    if (detected.keywords) {
      this.searchKeywords = detected.keywords;
    }

    this.loadWallpapers(ack.trim());
  }

  /* ═══════════════════════════════════════
     IMAGE UPLOAD
     ═══════════════════════════════════════ */

  handleWallUpload(event: any) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result as string;
      this.wallImage = data;
      this.activeUploadType = 'wall';
      this.panelState = 'expanded';
      this.chatHistory.push({ id: this.uid(), role: 'user', content: '\uD83D\uDCF7 Uploaded wall photo' });
      this.runAnalysis('wall', data);
      this.cdRef.detectChanges();
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }

  handleRefUpload(event: any) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result as string;
      this.referenceImage = data;
      this.activeUploadType = 'reference';
      this.panelState = 'expanded';
      this.chatHistory.push({ id: this.uid(), role: 'user', content: '\uD83D\uDDBC\uFE0F Uploaded inspiration image' });
      this.runAnalysis('reference', data);
      this.cdRef.detectChanges();
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }

  runAnalysis(type: 'wall' | 'reference', imageData: string) {
    this.isAnalyzing = true;
    this.analysisResult = null;

    this.wallpaperService.detectFiltersImage(imageData).subscribe({
      next: (detected: any) => {
        this.isAnalyzing = false;

        if (detected.space) {
          this.filters.room = detected.space;
          this.state.updateContext({ selected_space: detected.space });
        }
        if (detected.style) {
          this.filters.style = detected.style;
          this.state.updateContext({ selected_category: detected.style });
        }
        if (detected.mood) this.filters.mood = detected.mood;
        if (detected.keywords) this.searchKeywords = detected.keywords;

        this.phase = 'browsing';

        // Build detailed analysis text
        const parts: string[] = [];
        if (detected.space) parts.push(`Space: ${detected.space}`);
        if (detected.style) parts.push(`Style: ${detected.style}`);
        if (detected.mood) parts.push(`Mood: ${detected.mood}`);
        if (detected.keywords) parts.push(`Keywords: ${detected.keywords}`);

        const analysisText = type === 'wall'
          ? `I can see your ${detected.space || 'wall'}. ${detected.style ? `A ${detected.style.toLowerCase()} style would look beautiful here.` : ''} ${parts.length > 0 ? 'I detected: ' + parts.join(', ') + '.' : 'Which room is this?'}`
          : `I can see the style you're drawn to${detected.style ? ` \u2014 ${detected.style}` : ''}. ${parts.length > 0 ? 'I detected: ' + parts.join(', ') + '.' : ''} Which space are you decorating?`;

        this.analysisResult = analysisText.trim();

        // Determine next chips based on what's missing
        const nextChips = !this.filters.room
          ? ROOM_CHIPS.map(c => ({ id: c.id, label: c.label }))
          : !this.filters.style
            ? STYLE_CHIPS.map(c => ({ id: c.id, label: c.label }))
            : MOOD_CHIPS.map(c => ({ id: c.id, label: c.label }));

        this.loadWallpapers(this.analysisResult, nextChips);
        this.cdRef.detectChanges();
      },
      error: () => {
        this.isAnalyzing = false;
        this.phase = 'browsing';
        const fallback = type === 'wall'
          ? 'Your wall looks great. Which room is this wall in?'
          : 'Lovely inspiration. Which space are you decorating?';
        this.analysisResult = fallback;
        this.chatHistory.push({ id: this.uid(), role: 'assistant', content: fallback });
        this.guidance = { id: this.uid(), text: '', chips: ROOM_CHIPS.map(c => ({ id: c.id, label: c.label })) };
        this.scrollToBottom();
        this.cdRef.detectChanges();
      }
    });
  }

  /* ═══════════════════════════════════════
     WALLPAPER CLICK → Navigate to PDP
     ═══════════════════════════════════════ */

  onProductClick(id: string) {
    const wp = this.wallpaperCache.get(id);
    if (!wp) return;

    if (this.wallImage && this.previewWallpaper?.id !== wp.id) {
      this.previewWallpaper = wp;
      this.chatHistory.push({
        id: this.uid(), role: 'assistant',
        content: `Previewing ${wp.name} on your wall. Tap another to compare.`,
      });
      this.guidance = null;
      this.scrollToBottom();
    } else {
      // Track interaction
      this.state.updateContext({
        interaction_history: [...(this.state.context.interaction_history || []), String(wp.id)]
      });
      // Navigate to real PDP
      if (wp.collection_slug && wp.slug) {
        const colors = `${wp.max_color || '0'}-${wp.min_color || '0'}`;
        this.router.navigate(['/wallpaper', wp.collection_slug, wp.slug, colors]);
      } else {
        this.router.navigate(['/pdp', wp.slug || wp.id]);
      }
    }
  }

  /* ═══════════════════════════════════════
     RESET
     ═══════════════════════════════════════ */

  reset() {
    this.filters = { room: null, style: null, mood: null, color: null };
    this.results = [];
    this.phase = 'pick-room';
    this.wallSize = null;
    this.wallImage = null;
    this.referenceImage = null;
    this.previewWallpaper = null;
    this.guidance = null;
    this.showFilterPanel = false;
    this.panelState = 'normal';
    this.activeUploadType = null;
    this.analysisResult = null;
    this.isAnalyzing = false;
    this.chatHistory = [];
    this.isAiLoading = false;
    this.hasGreeted = false;
    this.filterChangeCount = 0;
    this.onPanelOpen();
  }

  /* ═══════════════════════════════════════
     HELPERS
     ═══════════════════════════════════════ */

  uid(): string {
    return Math.random().toString(36).slice(2, 10);
  }

  scrollToBottom() {
    setTimeout(() => {
      if (this.scrollRef?.nativeElement) {
        this.scrollRef.nativeElement.scrollTop = this.scrollRef.nativeElement.scrollHeight;
      }
    }, 100);
  }

  /** Lookup a wallpaper from cache by string ID. */
  getWallpaper(id: string): any {
    return this.wallpaperCache.get(id);
  }

  get activeFilterCount(): number {
    return [this.filters.room, this.filters.style, this.filters.mood, this.filters.color, this.searchKeywords].filter(Boolean).length;
  }

  get activeFilterPills(): string[] {
    return [this.filters.room, this.filters.style, this.filters.mood, this.filters.color, this.wallSize, this.searchKeywords].filter(Boolean) as string[];
  }

  get activeImage(): string | null {
    if (this.activeUploadType === 'wall') return this.wallImage;
    if (this.activeUploadType === 'reference' && this.referenceImage && this.referenceImage !== 'skipped') return this.referenceImage;
    return null;
  }

  isLastAssistant(index: number): boolean {
    for (let i = this.chatHistory.length - 1; i >= 0; i--) {
      if (this.chatHistory[i].role === 'assistant') return i === index;
    }
    return false;
  }

  detectWallSize(text: string): string | null {
    const lower = text.toLowerCase();
    if (/6\s*x\s*8/.test(lower)) return '6x8';
    if (/8\s*x\s*10/.test(lower)) return '8x10';
    if (/10\s*x\s*12/.test(lower)) return '10x12';
    if (lower.includes('large')) return '10x12';
    if (lower.includes('small')) return '6x8';
    if (lower.includes('medium')) return '8x10';
    return null;
  }

  detectFiltersFromText(text: string): any {
    const lower = text.toLowerCase();
    const detected: any = {};
    const roomMap: Record<string, string> = {
      'living room': 'Living Room', 'living': 'Living Room', 'bedroom': 'Bedroom',
      'kids': 'Kids Room', 'kid': 'Kids Room', 'kitchen': 'Kitchen',
      'dining': 'Dining Room', 'office': 'Office'
    };
    for (const key of Object.keys(roomMap)) {
      if (lower.includes(key)) { detected.space = roomMap[key]; break; }
    }
    const styleMap: Record<string, string> = {
      'floral': 'Florals', 'flower': 'Florals', 'modern': 'Modern', 'geometric': 'Modern',
      'minimal': 'Minimal', 'nature': 'Nature', 'botanical': 'Nature',
      'scenic': 'Scenic', 'landscape': 'Scenic', 'abstract': 'Abstract'
    };
    for (const key of Object.keys(styleMap)) {
      if (lower.includes(key)) { detected.style = styleMap[key]; break; }
    }
    const moodMap: Record<string, string> = {
      'calm': 'calm', 'serene': 'calm', 'luxury': 'luxury', 'bold': 'statement',
      'dramatic': 'statement', 'cozy': 'cozy', 'playful': 'playful', 'elegant': 'elegant'
    };
    for (const key of Object.keys(moodMap)) {
      if (lower.includes(key)) { detected.mood = moodMap[key]; break; }
    }
    return detected;
  }

  removeUpload() {
    if (this.activeUploadType === 'wall') {
      this.wallImage = null;
    } else {
      this.referenceImage = null;
    }
    this.activeUploadType = null;
    this.analysisResult = null;
    this.isAnalyzing = false;
    this.previewWallpaper = null;
    if (this.panelState === 'expanded') this.panelState = 'normal';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer?.files[0];
    if (file?.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        const data = reader.result as string;
        this.referenceImage = data;
        this.activeUploadType = 'reference';
        this.panelState = 'expanded';
        this.chatHistory.push({ id: this.uid(), role: 'user', content: '\uD83D\uDDBC\uFE0F Uploaded inspiration image' });
        this.runAnalysis('reference', data);
        this.cdRef.detectChanges();
      };
      reader.readAsDataURL(file);
    }
  }
}
